/**
 * Comprehensive list of resource types from ResourceTypeRegistry.cs
 * Shared between MigrationSetup and SnapshotSetup pages
 */

export interface ResourceTypeCategory {
  name: string;
  types: string[];
  labels?: Record<string, string>;
}

export const RESOURCE_TYPE_CATEGORIES: ResourceTypeCategory[] = [
  {
    name: 'Admin',
    types: [
      'User',
      'Group',
      'Location',
      'Station',
      'OAuth Client',
      'Group Roles',
      'Authorization Division',
      'Authorization Role',
      //'Authorization Product' Not supported as a resources in terraform
    ],
    labels: {
      'User': 'People',
      'Group': 'Groups',
      'Station': 'Phones',
      'OAuth Client': 'OAuth',
      'Authorization Division': 'Divisions',
      'Auth Division': 'Divisions',
      'Authorization Role': 'Roles',
      'Auth Role': 'Roles',
      //'Authorization Product': 'Subscriptions' Not supported as a resources in terraform
    }
  },
  {
    name: 'Architect',
    types: [
      'Architect Flow',
      'Architect Flow Milestone',
      'Architect Flow Outcome',
      'Architect Schedules',
      'Architect Schedule Groups',
      'Architect User Prompt',
      'Architect Datatable',
      'Architect Datatable (with Data)',
      'Architect Emergency Group',
      'Architect IVR',
      'Architect Grammar',
      'Architect Grammar Language'
    ],
    labels: {
      'Architect Flow': 'Flows',
      'Architect Flow Milestone': 'Flow Milestone',
      'Architect Flow Outcome': 'Flow Outcome',
      'Architect Schedules': 'Operating Schedule',
      'Architect Schedule Groups': 'Operating Schedule Group',
      'Architect User Prompt': 'User Prompts',
      'Architect Datatable': 'Data Table Schema',
      'Architect Datatable (with Data)': 'Data Table (with Data)',
      'Architect Emergency Group': 'Emergency Group',
      'Architect IVR': 'Call Route',
      'Architect Grammar': 'Grammar',
      'Architect Grammar Language': 'Grammar Language'
    }
  },
  {
    name: 'Routing',
    types: [
      'Routing Queue',
      'Routing Wrapup Code',
      'Routing Skill Group',
      'Routing Skill',
      'Routing Language',
      'Routing Language Skill',
      'Routing Email Domain',
      'Routing Email Route',
      'Routing SMS Address',
      'Routing Utilization',
      'Routing Utilization Label',
    ],
    labels: {
      'Routing Queue': 'Queues',
      'Routing Wrapup Code': 'Wrap-up Codes',
      'Routing Skill Group': 'Skill Group',
      'Routing Skill': 'ACD Skills',
      'Routing Language': 'Supported Languages',
      'Routing Language Skill': 'Language Skill',
      'Routing Email Domain': 'Email Domain',
      'Routing Email Route': 'Email Routes',
      'Routing SMS Address': 'Message Routing',
      'Routing Utilization': 'Utilization',
      'Routing Utilization Label': 'Utilization Label'
    }
  },
  {
    name: 'Outbound',
    types: [
      'Outbound Campaign',
      'Outbound Call Analysis Response Set',
      'Outbound Contact List',
      'Outbound Contact List Filter',
      'Outbound Contact List Template',
      'Outbound DNC List',
      'Outbound Attempt Limit',
      'Outbound Callable Time Set',
      'Outbound Campaign Rule',
      'Outbound Digital Ruleset',
      'Outbound File Specification Template',
      'Outbound Import Template',
      'Outbound Messaging Campaign',
      'Outbound Ruleset',
      'Outbound Sequence',
      'Outbound Settings'
    ]
  },
  {
    name: 'Telephony',
    types: [
      'Telephony Phone',
      'Telephony Phone Base Settings',
      'Telephony Site',
      'Telephony Edge',
      'Telephony Edge Group',
      'Telephony Trunk',
      'Telephony Trunk Base Settings',
      'Telephony Line Base Settings',
      'Telephony DID',
      'Telephony DID Pool',
      'Telephony Extension Pool',
      'Telephony Certification Authority',
      'Telephony Site Outbound Route',
    ],
    labels: {
      'Telephony Phone': 'Phone',
      'Telephony Phone Base Settings': 'Phone Base Settings',
      'Telephony Edge': 'Premises Edges',
      'Telephony Edge Group': 'Edge Group',
      'Telephony Trunk': 'External Trunk or Phone Trunk',
      'Telephony Line Base Settings': 'Line Appearance',
      'Telephony DID': 'DID Number',
      'Telephony DID Pool': 'DID Range',
      'Telephony Extension Pool': 'Extension Pools',
      'Telephony Certification Authority': 'Certificate Authority',
      'Telephony Site Outbound Route': 'Outbound Route'
    }
  },
  {
    name: 'Interaction Assets',
    types: [
      'Script',
      'Widget Deployment',
      'Response Management Library',
      'Response Management Response',
      'Response Management Response Asset'
    ],
    labels: {
      'Widget Deployment': 'Widgets',
      'Response Management Library': 'Canned Response Libraries',
      'Response Management Response': 'Canned Responses',
      'Response Management Response Asset': 'Response Assets'
    }
  },
  {
    name: 'Integrations',
    types: [
      'Integration',
      'Integration Action',
      'Web Deployment',
      'Web Deployment Configuration'
    ],
    labels: {
      'Integration': 'Integrations',
      'Integration Action': 'Data Actions',
      'Web Deployment': 'Web Messenger Deployments',
      'Web Deployment Configuration': 'Web Messenger Configurations'
    }
  },
  {
    name: 'Quality Management',
    types: [
      'Quality Evaluation Form',
      'Quality Survey Form'
    ],
    labels: {
      'Quality Evaluation Form': 'Evaluation Forms',
      'Quality Survey Form': 'Survey Form'
    }
  },
  {
    name: 'Recording and Policies',
    types: [
      'Recording Media Retention Policy'
    ],
    labels: {
      'Recording Media Retention Policy': 'Reorder Policies'
    }
  },
  {
    name: 'Speech and Text Analytics',
    types: [
      'Analytics Reporting Settings'
    ],
    labels: {
      'Analytics Reporting Settings': 'Analytics Settings'
    }
  },
  {
    name: 'Knowledge',
    types: [
      'Knowledge Base',
      'Knowledge Document',
      'Knowledge Document Variation',
      'Knowledge Category',
      'Knowledge Label'
    ],
    labels: {
      'Knowledge Document': 'Knowledge Articles',
      'Knowledge Document Variation': 'Knowledge Variation',
      'Knowledge Label': 'Knowledge Labels'
    }
  },
  {
    name: 'Employee Engagement',
    types: [
      'Employee Performance External Metrics'
    ],
    labels: {
      'Employee Performance External Metrics': 'External Metric'
    }
  },
  {
    name: 'Predictive Engagement',
    types: [
      'Journey Action Map',
      'Journey Action Template',
      'Journey Segment'
    ],
    labels: {
      'Journey Action Map': 'Journey Action Maps',
      'Journey Action Template': 'Journey Action Library',
      'Journey Segment': 'Segments'
    }
  },
  {
    name: 'Messaging',
    types: [
      'Messaging Integration Open',
      'Messaging Integration WhatsApp',
      'Messaging Settings Supported Content'
    ],
    labels: {
      'Messaging Integration Open': 'Open Messaging',
      'Messaging Integration WhatsApp': 'WhatsApp Messenger'
    }
  },
  {
    name: 'Contact Management',
    types: [
      'External Contact',
      'External Organization'
    ],
    labels: {
      'External Contact': 'Contacts',
      'External Organization': 'Organizations'
    }
  },
  {
    name: 'Organization',
    types: [
      'Organization Authentication Settings',
      'Organization Me',
      'Presence Definition',
      'Process Automation Trigger'
    ],
    labels: {
      'Organization Authentication Settings': 'Authentication',
      'Organization Me': 'Settings',
      'Process Automation Trigger': 'Trigger',
      'Presence Definition' : 'Status'
    }
  }
];

/**
 * Returns the display label for a resource type within a known category.
 * Falls back to the API name if no label is defined.
 */
export function getTypeLabel(category: ResourceTypeCategory, type: string): string {
  return category.labels?.[type] ?? type;
}

/**
 * Returns the display label for a resource type by API name alone.
 * Falls back to the API name if no label is defined.
 */
export function getTypeLabelByApiName(apiName: string): string {
  for (const category of RESOURCE_TYPE_CATEGORIES) {
    const label = category.labels?.[apiName];
    if (label !== undefined) return label;
  }
  return apiName;
}

// Flat list for backward compatibility
export const RESOURCE_TYPES = RESOURCE_TYPE_CATEGORIES.flatMap((category) => category.types);

export type ResourceType = (typeof RESOURCE_TYPES)[number];

/**
 * Resource types that belong to a specific division and can be created independently in each
 * division. In a Division-to-Division migration these are created fresh in the target division.
 */
export const DIVISION_SCOPED_RESOURCE_TYPES: string[] = [
  'Architect Flow',
  'Outbound Campaign',
  'Outbound DNC List',
  'Outbound Messaging Campaign',
  'External Contact',
  'External Organization',
];

/**
 * Resource types whose names are org-wide unique — the same name cannot exist in two
 * divisions simultaneously. In a Division-to-Division migration the existing resource has
 * its division_id reassigned to the target division (import + update) rather than a new
 * copy being created. They are selectable in the D2D picker but shown with an amber badge.
 *
 * Includes:
 *  - Routing Queue          — queue names are org-wide unique (proven by live D2D E2E test)
 *  - User                   — single identity per email across the entire org
 *  - Flow Milestone/Outcome — single-instance per name org-wide
 *  - The types below        — empirically proven org-wide unique 2026-07-21 via a live
 *                             create-probe (a duplicate-name create in a second division
 *                             returns HTTP 400/409). They reassign, not duplicate.
 *
 * NOTE: Script is here despite the same name being allowed in two divisions. The Terraform
 * provider's getScriptIdByNameFn does an org-wide name lookup that fails when duplicates exist,
 * so scripts must use the Reassign path (import by UUID) to bypass the name lookup entirely.
 */
export const DIVISION_ORG_UNIQUE_RESOURCE_TYPES: string[] = [
  'Routing Queue',
  'User',
  'Architect Flow Milestone',
  'Architect Flow Outcome',
  'Routing Wrapup Code',
  'Routing Skill Group',
  'Architect Datatable',
  'Architect Datatable (with Data)',
  'Architect Emergency Group',
  'Architect IVR',
  'Architect Schedules',
  'Architect Schedule Groups',
  'Outbound Contact List',
  'Script',
];

/**
 * Full set of resource types visible in Division-to-Division mode.
 * Division-scoped types (can be duplicated) + org-unique types (will be reused).
 */
export const D2D_VISIBLE_RESOURCE_TYPES: string[] = [
  ...DIVISION_SCOPED_RESOURCE_TYPES,
  ...DIVISION_ORG_UNIQUE_RESOURCE_TYPES,
];

/**
 * The kind of migration caveat that applies to a resource type. These mirror the
 * backend Importer remediation buckets (secrets, singleton settings, system objects,
 * unpublished/draft assets) plus the UI-only prefix-rename constraint.
 */
export type ResourceCaveatKind =
  | 'secrets-blank'
  | 'unpublished-skipped'
  | 'singleton-overwrite'
  | 'system-object'
  | 'no-renameable-name'
  | 'draft-vs-published';

/** Visual tone that drives a caveat badge's color. */
export type ResourceCaveatTone = 'warning' | 'info' | 'positive';

export interface ResourceCaveat {
  kind: ResourceCaveatKind;
  /** Short badge text shown inline next to the resource type. */
  label: string;
  /** Full explanation shown on hover as a tooltip. */
  tooltip: string;
  /** Visual tone driving the badge color. */
  tone: ResourceCaveatTone;
}

const SECRETS_BLANK: ResourceCaveat = {
  kind: 'secrets-blank',
  label: 'Secrets blanked',
  tooltip:
    'Secret values (client secrets, credentials, tokens) cannot be exported. The resource is created with blank secrets — re-enter them manually in the target org after import.',
  tone: 'warning'
};

const SINGLETON_OVERWRITE: ResourceCaveat = {
  kind: 'singleton-overwrite',
  label: 'Overwrites org setting',
  tooltip:
    'This is a single, org-wide settings object. Importing it overwrites the existing value in the target org rather than creating a new resource.',
  tone: 'warning'
};

const SYSTEM_OBJECT: ResourceCaveat = {
  kind: 'system-object',
  label: 'Built-in objects referenced',
  tooltip:
    'Built-in objects (e.g. the Home division, the Master Admin user) are referenced in the target org, not recreated. Only custom items of this type are migrated.',
  tone: 'info'
};

const UNPUBLISHED_SKIPPED: ResourceCaveat = {
  kind: 'unpublished-skipped',
  label: 'Published only',
  tooltip:
    'Only published flows are exported and imported. Unpublished or draft-only flows produce an empty export and are skipped.',
  tone: 'info'
};

const DRAFT_VS_PUBLISHED: ResourceCaveat = {
  kind: 'draft-vs-published',
  label: 'Published version',
  tooltip:
    'Only the published version of this asset is migrated. Unpublished draft edits in the source org are not carried over.',
  tone: 'info'
};

const NO_RENAMEABLE_NAME: ResourceCaveat = {
  kind: 'no-renameable-name',
  label: 'Prefix not applicable',
  tooltip:
    'This resource type has no user-facing renameable name, so an Add Prefix transformation is a no-op and is disabled for it.',
  tone: 'warning'
};

/**
 * Per-type migration caveats, keyed by the UI display-name type strings used in
 * RESOURCE_TYPE_CATEGORIES. Drives the caveat badges in the resource selector and the
 * Add Prefix property constraint. Types with no entry have no caveats.
 */
export const RESOURCE_TYPE_CAVEATS: Record<string, ResourceCaveat[]> = {
  'OAuth Client': [SECRETS_BLANK],
  'Integration': [SECRETS_BLANK],
  'Organization Authentication Settings': [SINGLETON_OVERWRITE],
  'Outbound Settings': [SINGLETON_OVERWRITE],
  'Authorization Division': [SYSTEM_OBJECT],
  'Architect Flow': [UNPUBLISHED_SKIPPED],
  'Knowledge Document': [DRAFT_VS_PUBLISHED],
  'Architect Grammar Language': [NO_RENAMEABLE_NAME],
  'Telephony DID Pool': [NO_RENAMEABLE_NAME],
  'Telephony Extension Pool': [NO_RENAMEABLE_NAME],
  'Routing Email Domain': [NO_RENAMEABLE_NAME]
};

/** Returns the migration caveats for a resource type (empty array if none). */
export function getResourceCaveats(type: string): ResourceCaveat[] {
  return RESOURCE_TYPE_CAVEATS[type] ?? [];
}

/**
 * True when the resource type has no user-facing renameable name, so an Add Prefix
 * transformation cannot meaningfully apply to it.
 */
export function hasNoRenameableName(type: string): boolean {
  return getResourceCaveats(type).some((c) => c.kind === 'no-renameable-name');
}
